import { useEffect, useReducer, useCallback, useMemo } from 'react';

import { produce } from 'immer';

import {
  fetchWithToken,
  getRelationshipServiceUrl,
} from '@adsk/acs-sc-web-platform-api';
import { useCurrentProjectId } from '@adsk/alloy-provider';

import useMemoizedStringArray from './useMemoizedStringArray';
import usePendingIds from './usePendingIds';
import { addRelationshipsToState } from '../helpers/addRelationshipsToState';
import { useReferencePickerPlugins } from '../providers/ReferencePickerPluginsProvider';
import {
  Relationship,
  Entity,
  RelationshipsEntityMap,
  RelationshipDataBySource,
} from '../types/types';

enum ACTIONS {
  FETCH_INIT = 'FETCH_INIT',
  FETCH_SUCCESS = 'FETCH_SUCCESS',
  EMPTY_RESPONSE = 'EMPTY_RESPONSE',
  FETCH_FAILURE = 'FETCH_FAILURE',
  ADD_RELATIONSHIPS = 'ADD_RELATIONSHIPS',
  REMOVE_RELATIONSHIP = 'REMOVE_RELATIONSHIP',
}

interface FetchInitAction {
  type: ACTIONS.FETCH_INIT;
  payload: {
    ids: string[];
    domain: string;
    type: string;
  };
}

interface FetchSuccessAction {
  type: ACTIONS.FETCH_SUCCESS;
  payload: {
    ids: string[];
    domain: string;
    type: string;
    continuationToken?: string | null;
    relationships: Relationship[];
  };
}

interface EmptyResponseAction {
  type: ACTIONS.EMPTY_RESPONSE;
  payload: {
    ids: string[];
    domain: string;
    type: string;
  };
}

interface FetchFailureAction {
  type: ACTIONS.FETCH_FAILURE;
  payload: {
    ids: string[];
    domain: string;
    type: string;
    error: boolean;
    retry: () => void;
  };
}

interface AddRelationshipsAction {
  type: ACTIONS.ADD_RELATIONSHIPS;
  payload: {
    domain: string;
    type: string;
    relationships: Relationship[];
  };
}

interface RemoveRelationshipAction {
  type: ACTIONS.REMOVE_RELATIONSHIP;
  payload: string;
}

type Action =
  | FetchInitAction
  | FetchSuccessAction
  | EmptyResponseAction
  | FetchFailureAction
  | AddRelationshipsAction
  | RemoveRelationshipAction;

type AllowedEntities = Pick<Entity, 'domain' | 'type'>;

interface ReadDataResponse {
  relationships: Relationship[];
  page: Record<string, string | null>;
}

interface State {
  relationshipsEntityMap: RelationshipsEntityMap;
  relationshipDataBySource: RelationshipDataBySource;
}

export interface UseGetAllRelationshipsReturnType {
  relationshipsEntityMap: RelationshipsEntityMap;
  relationshipDataBySource: RelationshipDataBySource;
  fetchByIds: (ids: string[], force?: boolean) => void;
  addRelationships: (relationships: Relationship[]) => void;
  removeRelationshipById: (relationshipId: string) => void;
}

const initialState: State = {
  /**
   * This object holds relationship entities by relationship ID.
   *
   * Something like:
   * relationshipsEntityMap: {
   *   '123': {
   *     id: '123',
   *     entities: [{
   *       id: '456',
   *       domain: 'autodesk-bim360-documentmanagement',
   *       type: 'documentversion'
   *     }, {
   *       id: '789',
   *       domain: 'autodesk-construction-sheet',
   *       type: 'sheetversion'
   *     }]
   *   }
   * }
   */
  relationshipsEntityMap: {},
  /**
   * This object holds array of relationship data per source entity - domain, type & ID.
   *
   * Something like:
   * relationshipDataBySource: {
   *   'autodesk-bim360-documentmanagement': {
   *     'documentversion': {
   *       '1234': {
   *          relationshipsData: [{
   *            relationshipId: '123',
   *            domain: 'autodesk-construction-sheet',
   *            type: 'sheetversion',
   *            entityId: '121314',
   *          }, {
   *            relationshipId: '456',
   *            domain: 'autodesk-construction-sheet',
   *            type: 'sheetversion',
   *            entityId: '151617',
   *          }],
   *          loading: false,
   *          error: null,
   *          retry: null,
   *       }
   *     }
   *   }
   * }
   */
  relationshipDataBySource: {},
};

const dataReducer = (state: State, action: Action) =>
  produce(state, (nextState) => {
    switch (action.type) {
      case ACTIONS.FETCH_INIT: {
        const { ids, domain, type } = action.payload;
        if (!nextState.relationshipDataBySource[domain]) {
          nextState.relationshipDataBySource[domain] = {};
        }
        if (!nextState.relationshipDataBySource[domain][type]) {
          nextState.relationshipDataBySource[domain][type] = {};
        }
        ids.forEach((id) => {
          if (!nextState.relationshipDataBySource[domain][type][id]) {
            nextState.relationshipDataBySource[domain][type][id] = {
              relationshipsData: [],
            };
          }
          nextState.relationshipDataBySource[domain][type][id].loading = true;
          nextState.relationshipDataBySource[domain][type][id].error = false;
          nextState.relationshipDataBySource[domain][type][id].retry =
            undefined;
        });
        break;
      }
      case ACTIONS.FETCH_SUCCESS: {
        const { ids, domain, type, continuationToken, relationships } =
          action.payload;
        addRelationshipsToState(relationships, domain, type, nextState);
        ids.forEach((id) => {
          nextState.relationshipDataBySource[domain][type][id].loading =
            !!continuationToken;
        });
        break;
      }
      case ACTIONS.ADD_RELATIONSHIPS: {
        const { domain, type, relationships } = action.payload;
        addRelationshipsToState(relationships, domain, type, nextState, true);
        break;
      }
      case ACTIONS.EMPTY_RESPONSE: {
        const { ids, domain, type } = action.payload;
        ids.forEach((id) => {
          nextState.relationshipDataBySource[domain][type][id].loading = false;
          nextState.relationshipDataBySource[domain][type][
            id
          ].relationshipsData = [];
        });
        break;
      }
      case ACTIONS.FETCH_FAILURE: {
        const { ids, domain, type, retry } = action.payload;
        ids.forEach((id) => {
          nextState.relationshipDataBySource[domain][type][id].loading = false;
          nextState.relationshipDataBySource[domain][type][id].error = true;
          nextState.relationshipDataBySource[domain][type][id].retry = retry;
        });
        break;
      }
      case ACTIONS.REMOVE_RELATIONSHIP: {
        const relationshipIdToRemove = action.payload;
        const relationshipToRemove =
          nextState.relationshipsEntityMap[relationshipIdToRemove];
        delete nextState.relationshipsEntityMap[relationshipIdToRemove];
        if (relationshipToRemove) {
          relationshipToRemove.entities.forEach(({ domain, type, id }) => {
            const existingSourceData =
              nextState.relationshipDataBySource[domain]?.[type]?.[id];
            if (existingSourceData) {
              existingSourceData.relationshipsData =
                existingSourceData.relationshipsData.filter(
                  ({ relationshipId }) =>
                    relationshipId !== relationshipIdToRemove,
                );
            }
          });
        }
        break;
      }
      default:
        throw new Error();
    }
  });

const getUrl = (baseUrl: string, projectId?: string) => {
  if (!baseUrl || !projectId) {
    return null;
  }
  return `${baseUrl}/v2/containers/${projectId}/relationships:intersect`;
};

const RELATIONSHIP_INTERSECT_PAGE_SIZE = 20;
const MAX_WITH_ENTITIES_PER_REQUEST = 30;

/**
 * This hook returns all relevant relationships for given sourceEntityType & sourceEntityId.
 * Returned value would consist of all relationships, eagerly fetching all paginated data.
 *
 * Returned value:
 * relationships - an array including all relationships related to given source type & ID.
 * loading - whether it currently loads data from BE.
 * error - whether fetching data raised an error.
 * retry - callback to trigger re-fetch upon error.
 * addRelationships - callback to add new relationship. Expects one argument - an array of new relationships to add.
 * removeRelationship - callback to remove relationship. Expects one argument - the relationship to remove.
 *
 * @param domain - the source entity domain.
 * @param type - the source entity type.
 * @param idsArg - an array of the source entity IDs to get relationships for.
 * @param disableRelationshipService - whether we want to disable fetching data from relationship service.
 * @param relationshipEntitiesList - list of relationship entities to add to internal state.
 * @returns {{loading: *, relationships: *, triggerFetch: function}}
 */
export default (
  domain: string,
  type: string,
  idsArg?: string[],
  disableRelationshipService?: boolean,
  relationshipEntitiesList?: Relationship[],
): UseGetAllRelationshipsReturnType => {
  const projectId = useCurrentProjectId();
  const relationshipUrl = getRelationshipServiceUrl();
  const urlWithProject = getUrl(relationshipUrl, projectId);
  const [state, dispatch] = useReducer(dataReducer, initialState);
  const { pendingIdsArray, addPendingIds, clearPendingIds } = usePendingIds();
  const memoizedIds = useMemoizedStringArray(idsArg || []);

  const { allowedPlugins, loadingPlugins } = useReferencePickerPlugins();

  const withEntitiesChunks = useMemo(() => {
    const allowedEntities: AllowedEntities[] = [];
    allowedPlugins.filter(Boolean).forEach((plugin) => {
      if (
        allowedEntities
          .filter(Boolean)
          .find(
            (entity) =>
              entity.domain === plugin.metadata.domain &&
              entity.type === plugin.metadata.type,
          )
      ) {
        return;
      }

      // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator array built within this hook
      allowedEntities.push({
        domain: plugin.metadata.domain,
        type: plugin.metadata.type,
      });
    });

    // map on plugins and split to chunks of 30 plugins
    return allowedEntities.reduce(
      (acc: Array<AllowedEntities[]>, entity, index) => {
        const chunkIndex = Math.floor(index / MAX_WITH_ENTITIES_PER_REQUEST);

        if (!acc[chunkIndex]) {
          acc[chunkIndex] = [];
        }

        acc[chunkIndex].push(entity);
        return acc;
      },
      [],
    );
  }, [allowedPlugins]);

  useEffect(() => {
    addPendingIds(memoizedIds);
  }, [addPendingIds, memoizedIds]);

  useEffect(() => {
    if (relationshipEntitiesList) {
      dispatch({
        type: ACTIONS.FETCH_SUCCESS,
        payload: {
          ids: [],
          domain,
          type,
          relationships: relationshipEntitiesList,
        },
      });
    }
  }, [relationshipEntitiesList, domain, type]);

  const readData = useCallback(
    (
      url: string,
      continuationToken: string | null,
      ids: string[],
      domain: string,
      type: string,
      withEntities: AllowedEntities[],
    ) => {
      if (ids.length) {
        const urlToUse = continuationToken
          ? `${url}?continuationToken=${continuationToken}`
          : url;
        const body = {
          withEntities,
          entities: ids.map((id) => ({ id, type, domain })),
        };
        dispatch({
          type: ACTIONS.FETCH_INIT,
          payload: { ids, domain, type },
        });
        return fetchWithToken<ReadDataResponse>(urlToUse, {
          method: 'POST',
          headers: {
            'content-type': 'application/json; charset=utf-8',
          },
          body: JSON.stringify(body),
        })
          .then((res) => {
            const { relationships, page } = res;
            const nextContinuationToken = page?.continuationToken;
            dispatch({
              type: ACTIONS.FETCH_SUCCESS,
              payload: {
                ids,
                domain,
                type,
                continuationToken: nextContinuationToken,
                relationships,
              },
            });
            if (nextContinuationToken) {
              readData(
                url,
                nextContinuationToken,
                ids,
                domain,
                type,
                withEntities,
              );
            }
          })
          .catch((error) => {
            if (error.response?.status === 404) {
              dispatch({
                type: ACTIONS.EMPTY_RESPONSE,
                payload: { ids, domain, type },
              });
            } else {
              console.error(
                'ReferencePicker - useGetAllRelationships: Error getting relationships',
                error,
              );
              // This is needed as we hold error per entity, and we might need to trigger retry for multiple entities.
              // So here we mark whether the retry method was called or not.
              let retryCalled = false;
              dispatch({
                type: ACTIONS.FETCH_FAILURE,
                payload: {
                  ids,
                  domain,
                  type,
                  error,
                  retry: () => {
                    if (!retryCalled) {
                      readData(
                        url,
                        continuationToken,
                        ids,
                        domain,
                        type,
                        withEntities,
                      );
                      retryCalled = true;
                    }
                  },
                },
              });
            }
          });
      }

      return Promise.resolve();
    },
    [],
  );

  useEffect(() => {
    const callReadDataWithEntitiesOneByOne = async (idsChunk: string[]) => {
      for (const withEntities of withEntitiesChunks) {
        if (!urlWithProject) return;
        await readData(
          urlWithProject,
          null,
          idsChunk,
          domain,
          type,
          withEntities,
        );
      }
    };

    if (
      urlWithProject &&
      pendingIdsArray.length &&
      !disableRelationshipService &&
      !loadingPlugins
    ) {
      for (
        let i = 0;
        i < pendingIdsArray.length;
        i += RELATIONSHIP_INTERSECT_PAGE_SIZE
      ) {
        const pendingIdsChunk = pendingIdsArray.slice(
          i,
          i + RELATIONSHIP_INTERSECT_PAGE_SIZE,
        );

        if (withEntitiesChunks && withEntitiesChunks.length) {
          void callReadDataWithEntitiesOneByOne(pendingIdsChunk);
        }
      }
      clearPendingIds();
    }
  }, [
    readData,
    urlWithProject,
    pendingIdsArray,
    domain,
    type,
    clearPendingIds,
    disableRelationshipService,
    loadingPlugins,
    withEntitiesChunks,
  ]);

  const addRelationships = useCallback(
    (relationships: Relationship[]) => {
      dispatch({
        type: ACTIONS.ADD_RELATIONSHIPS,
        payload: { relationships, domain, type },
      });
    },
    [domain, type],
  );

  const removeRelationshipById = useCallback((relationshipId: string) => {
    dispatch({ type: ACTIONS.REMOVE_RELATIONSHIP, payload: relationshipId });
  }, []);

  return {
    relationshipsEntityMap: state.relationshipsEntityMap,
    relationshipDataBySource: state.relationshipDataBySource,
    fetchByIds: addPendingIds,
    addRelationships,
    removeRelationshipById,
  };
};
